import { LocalStorageCachedDataKeysHMI } from "constants/constants";
import { useContext, useEffect, useMemo, useState } from "react";
import { HMICachedDataContext } from "../contexts/HMICachedDataContext/HMICachedDataContext";
import {
    getLatestCompletedInterval,
    getScrapCountForInterval,
} from "../utilities/qualityIntervals";

const RECHECK_INTERVAL_MS = 60_000;

const ackKey = (machineCenterId: number): string =>
    `${LocalStorageCachedDataKeysHMI.MANUAL_QUALITY_LAST_ACK}:${machineCenterId}`;

const readAck = (machineCenterId: number): string | null => {
    try {
        return localStorage.getItem(ackKey(machineCenterId));
    } catch {
        return null;
    }
};

const writeAck = (machineCenterId: number, hourAlignedIntervalStartIso: string): void => {
    try {
        localStorage.setItem(ackKey(machineCenterId), hourAlignedIntervalStartIso);
    } catch {
        // ignore — the worst case is a re-flash next mount, harmless.
    }
};

export interface IUseQualityFlash {
    /** Whether the Quality button should currently flash. */
    flashing: boolean;
    /**
     * Marks the most-recently completed interval as acknowledged so the
     * button stops flashing immediately (optimistic). Restarts on the next
     * interval-completion if it remains unlogged.
     */
    acknowledge(): void;
}

/**
 * Drives the Quality button's flashing alert. The button flashes when the
 * latest completed interval (full or partial; current shift up to "now"
 * plus all of previous shift) has zero scrap recorded AND the operator
 * hasn't already acknowledged that interval.
 *
 * Acknowledgement is persisted in localStorage so a page reload doesn't
 * resurrect a flash for an interval the operator already addressed.
 */
export const useQualityFlash = (
    machineCenterId: number | undefined,
    enabled: boolean,
): IUseQualityFlash => {
    const { store } = useContext(HMICachedDataContext);
    const [now, setNow] = useState<Date>(() => new Date());
    const [acknowledgedIso, setAcknowledgedIso] = useState<string | null>(() =>
        machineCenterId !== undefined ? readAck(machineCenterId) : null,
    );

    // Re-tick once a minute so we re-evaluate intervals near the hour boundary.
    useEffect(() => {
        if (!enabled) {
            return;
        }
        const interval = setInterval(() => setNow(new Date()), RECHECK_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [enabled]);

    // Refresh acknowledgement state when machineCenterId changes.
    useEffect(() => {
        if (machineCenterId === undefined) {
            setAcknowledgedIso(null);
            return;
        }
        setAcknowledgedIso(readAck(machineCenterId));
    }, [machineCenterId]);

    const lastTwoShifts = store?.lastTwoShiftsEventReports;

    const latestInterval = useMemo(
        () =>
            getLatestCompletedInterval(
                lastTwoShifts?.previousShiftStart,
                lastTwoShifts?.previousShiftEnd,
                lastTwoShifts?.currentShiftStart,
                lastTwoShifts?.currentShiftEnd,
                now,
            ),
        [
            lastTwoShifts?.previousShiftStart,
            lastTwoShifts?.previousShiftEnd,
            lastTwoShifts?.currentShiftStart,
            lastTwoShifts?.currentShiftEnd,
            now,
        ],
    );

    const flashing = useMemo(() => {
        if (!enabled || !latestInterval) {
            return false;
        }
        if (acknowledgedIso === latestInterval.hourAlignedIntervalStartIso) {
            return false;
        }
        const scrap = getScrapCountForInterval(
            latestInterval.hourAlignedIntervalStartIso,
            latestInterval.kind,
            lastTwoShifts?.currentShiftQualitySummary,
            lastTwoShifts?.previousShiftQualitySummary,
        );
        return scrap === 0;
    }, [
        enabled,
        latestInterval,
        acknowledgedIso,
        lastTwoShifts?.currentShiftQualitySummary,
        lastTwoShifts?.previousShiftQualitySummary,
    ]);

    const acknowledge = () => {
        if (machineCenterId === undefined || !latestInterval) {
            return;
        }
        writeAck(machineCenterId, latestInterval.hourAlignedIntervalStartIso);
        setAcknowledgedIso(latestInterval.hourAlignedIntervalStartIso);
    };

    return { acknowledge, flashing };
};
